import { useEffect, useRef } from 'react';

/**
 * How long a device sits untouched before it locks itself.
 *
 * Short on purpose. A tablet left on a pass under someone's PIN is how a round
 * ends up on the wrong waiter's name, and unlocking costs four digits.
 */
export const IDLE_LOCK_MINUTES = 3;

/** Anything that counts as someone still using the device. */
const ACTIVITY = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

/**
 * Call `onIdle` once the device has gone untouched for `minutes`.
 *
 * The timer is reset by real input rather than by rendering, so a screen that
 * polls in the background does not keep the device unlocked on its own.
 */
export function useIdleLock(onIdle: () => void, enabled: boolean, minutes = IDLE_LOCK_MINUTES) {
    const timer = useRef<number | undefined>(undefined);
    // Held in a ref so a changing callback identity cannot restart the countdown.
    const fire = useRef(onIdle);
    fire.current = onIdle;

    useEffect(() => {
        if (!enabled) return;

        const ms = minutes * 60 * 1000;

        const reset = () => {
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => fire.current(), ms);
        };

        reset();
        ACTIVITY.forEach(evt => window.addEventListener(evt, reset, { passive: true }));

        return () => {
            window.clearTimeout(timer.current);
            ACTIVITY.forEach(evt => window.removeEventListener(evt, reset));
        };
    }, [enabled, minutes]);
}
